import { Upload } from "antd";
import { forwardRef, useMemo, useRef } from "react";
import {
  CloudUploadOutlined,
  DeleteOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { UploadChangeParam } from "antd/es/upload/interface";
import { RcFile } from "antd/lib/upload";
import { useTranslation } from "react-i18next";
import { UploadRef } from "antd/es/upload/Upload";

import BaseError from "../BaseError";
import { BaseUploadProps } from "./types";
import { BaseFormError } from "../types";

import "./styles.css";
import BaseButton from "@/components_v2/Button/BaseButton";

const { Dragger } = Upload;

const BaseUpload = forwardRef<UploadRef, BaseUploadProps>(
  (
    {
      value,
      onChange,
      uploadFn,
      onUploadStart,
      onUploadFinish,
      onUploadError,
      maxFileSize,
      supportedExtensions,
      multiple,
      shouldShowValues = true,
      error,
      helpText,
      icon = <CloudUploadOutlined />,
      ...props
    }: BaseUploadProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const uploadingRef = useRef<Set<string>>(new Set());

    const handleUploadFunc = async (info: UploadChangeParam) => {
      if (!uploadFn) return;

      const newFiles = multiple
        ? info.fileList
            .map((file) => file.originFileObj)
            .filter(
              (file): file is RcFile =>
                file != null && !uploadingRef.current.has(file.uid),
            )
        : !uploadingRef.current.has(info.file.uid)
          ? [info.file]
          : [];

      if (newFiles.length === 0) return;
      newFiles.forEach((file) => uploadingRef.current.add(file.uid));

      try {
        if (onUploadStart) {
          onUploadStart(newFiles);
        }

        const { successful, failed } = await uploadFn(newFiles);

        if (onUploadFinish) {
          onUploadFinish(successful);
        }

        if (onUploadError) {
          onUploadError(failed);
        }

        if (multiple) {
          onChange([...(value || []), ...successful.map((i) => i.id)]);
        } else {
          const firstResponse = successful[0];
          onChange(firstResponse?.id);
        }
      } finally {
        newFiles.forEach((file) => uploadingRef.current.delete(file.uid));
      }
    };

    // If upload function is provided, upload the files and send the response to the callback, otherwise just set them to the form
    const handleChange = async (info: UploadChangeParam) => {
      if (uploadFn) {
        await handleUploadFunc(info);
      } else {
        if (multiple) {
          onChange(info.fileList);
        } else {
          onChange(info.file);
        }
      }
    };

    const handleDelete = (index: number) => {
      if (multiple) {
        onChange(
          value.filter((_: never, fileIndex: number) => fileIndex !== index),
        );
      } else {
        onChange(null);
      }
    };

    const label = useMemo(() => {
      let label = helpText ? helpText + " " : "";
      if (supportedExtensions) {
        const extensions = supportedExtensions
          .map((i) => i.replace(".", "").toUpperCase())
          .join(", ");
        label += t("form.validation.supportedExtensions", { extensions });
      }
      if (maxFileSize) {
        label += `, ${t("form.validation.maxFileSize", { fileSize: Math.floor(maxFileSize / 1000000) })}`;
      }

      if (label.trim()) {
        label += ".";
      }

      return label;
    }, [supportedExtensions, maxFileSize]);

    return (
      <div className="base-upload">
        {value && !uploadFn && shouldShowValues && (
          <div className="upload-file-list">
            {(multiple ? value : [value]).map((i: File, index: number) => (
              <div key={i.name} className="flex items-center full-width">
                <div className="flex items-center justify-center">
                  <FileOutlined />
                </div>
                <div className="upload-file-name">{i.name}</div>
                <div className="upload-file-delete flex items-center justify-center">
                  <BaseButton
                    variant="link"
                    leadingIcon={<DeleteOutlined />}
                    onClick={() => handleDelete(index)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <Dragger
          className={error && "base-uploader-error"}
          multiple={multiple}
          beforeUpload={() => false} // Needed in order to stop automatically posting file
          accept={
            supportedExtensions ? supportedExtensions.join(",") : undefined
          }
          showUploadList={false}
          {...props}
          ref={ref}
          fileList={[]}
          onChange={handleChange}
        >
          <p className="ant-upload-drag-icon">{icon}</p>

          <p className="ant-upload-text">
            {t("labels.uploaderDescription", { plural: multiple ? "s" : "" })}
          </p>

          {label && <p className="upload-label">{label}</p>}
        </Dragger>
        <BaseError error={error as BaseFormError} />
      </div>
    );
  },
);

export default BaseUpload;
